import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FolderOpen, FileText, TrendingUp, Trophy, ArrowRight, Settings, Users as UsersIcon, Package, Boxes, MessageSquare, BookOpen, Percent, Heart, Key, Download, Calendar as CalendarIcon, ListTodo, Wrench, GraduationCap, Loader2, Lock, Unlock, AlertTriangle, AlertOctagon, FlaskConical, Table as TableIcon, Receipt, Inbox, Building2, ClipboardList, Layers, Gavel, Sun, Phone, GitBranch, Shield, ShieldCheck, BarChart3, Network, Eraser, Gift, RefreshCw, Mail, ListChecks, Flame } from "lucide-react";
import { isAdmin, isDirectorOrAbove, isManagerOrAbove } from "@/lib/roles";
import { useUserCaps } from "@/lib/useUserCaps";
import { toast } from "sonner";
import { useTrainingMode } from "@/contexts/TrainingModeContext";
import { PendingInstallerBanner } from "@/components/PendingInstallerBanner";
import { UserActivityTile } from "@/components/activity/UserActivityTile";
import { SmsHealthWarning } from "@/components/sms/SmsHealthWarning";
import { DashboardManagerNav } from "@/components/DashboardManagerNav";
import { ManagerProjectTable } from "@/components/ManagerProjectTable";
import { SchulungOnboardingModal, hasSeenSchulungOnboarding } from "@/components/SchulungOnboardingModal";
import api, { BACKEND_URL } from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantCapabilities } from "@/contexts/TenantCapsContext";
import { AppHeader } from "@/components/AppHeader";
import PasskeyPromptBanner from "@/components/PasskeyPromptBanner";
import ProjectPipeline from "@/components/ProjectPipeline";
import TelesalesDashboard from "@/pages/TelesalesDashboard";

const STATUS_COLORS = {
  "Erstkontakt": "bg-zinc-700",
  "Termin geplant": "bg-blue-600",
  "Vor-Ort-Aufnahme": "bg-indigo-600",
  "Technische Prüfung": "bg-violet-600",
  "Angebot erstellt": "bg-amber-600",
  "Angebot versendet": "bg-orange-600",
  "Nachfassen": "bg-pink-600",
  "Gewonnen": "bg-[#00E575] text-[#09090B]",
  "Verloren": "bg-red-700",
  "Archiviert": "bg-zinc-600",
};

const StatCard = ({ icon: Icon, label, value, accent, testid, compact = false }) => (
  <div data-testid={testid} className={`bg-[#18181B] border border-zinc-800 rounded-2xl hover:border-zinc-700 transition ${compact ? "p-3" : "p-5"}`}>
    {compact ? (
      <div className="flex items-center gap-3">
        <Icon className={accent || "text-zinc-400"} size={18} />
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold tracking-tight leading-none">{value}</div>
          <div className="text-[12px] text-zinc-400 uppercase tracking-wider mt-0.5 truncate">{label}</div>
        </div>
      </div>
    ) : (
      <>
        <div className="flex items-start justify-between mb-4">
          <Icon className={accent || "text-zinc-400"} size={22} />
        </div>
        <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
        <div className="text-xs text-zinc-400 uppercase tracking-wider mt-1">{label}</div>
      </>
    )}
  </div>
);

// Compact horizontal-scroll nav pill. Renders either a <Link> (internal), <a>
// (external), or a <button> based on which prop is set. Optional badge for
// pending counters. accent variants tweak hover colour for special items.
const NavPill = ({ testid, to, href, external, onClick, icon: Icon, label, badge, accent }) => {
  const accentMap = {
    green: "hover:border-[#00E575] hover:text-[#00E575]",
    amber: "border-amber-500/40 text-amber-300 hover:border-amber-400 hover:text-amber-200",
    sky: "hover:border-sky-500/60 hover:text-sky-300",
  };
  const accentClass = accentMap[accent] || "hover:border-[#00E575]/60 hover:text-[#00E575]";
  const base =
    "snap-start shrink-0 inline-flex items-center gap-2 bg-[#18181B] border border-zinc-700 text-zinc-200 rounded-xl px-4 h-12 active:scale-[0.97] transition font-medium text-[14px] whitespace-nowrap relative " + accentClass;
  const content = (
    <>
      <Icon size={16} />
      {label}
      {badge != null && (
        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-[#09090B] text-[12px] font-bold">
          {badge}
        </span>
      )}
    </>
  );
  if (to) return <Link to={to} data-testid={testid} className={base}>{content}</Link>;
  if (href) return <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} data-testid={testid} className={base}>{content}</a>;
  return <button type="button" onClick={onClick} data-testid={testid} className={base + " text-left"}>{content}</button>;
};

const MONTH_LABELS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

// Themen-Gruppen für das Manager/Admin-Dashboard.
// Reihenfolge bestimmt die visuelle Position der Sektionen.
const DASHBOARD_GROUPS = {
  daily:     { order: 1,  label: "Mein Tag",                  accent: "green"  },
  ops:       { order: 2,  label: "Operations & Pipeline",     accent: "green"  },
  people:    { order: 3,  label: "Personen & Teams",          accent: "sky"    },
  catalog:   { order: 4,  label: "Kataloge & Produkte",       accent: "amber"  },
  config:    { order: 5,  label: "System-Konfiguration",      accent: "violet" },
  marketing: { order: 6,  label: "Marketing & Statistik",     accent: "amber"  },
  learning:  { order: 7,  label: "Lernen & Quiz",             accent: "sky"    },
  partners:  { order: 8,  label: "Partner & Mandanten",       accent: "violet" },
  security:  { order: 9,  label: "Sicherheit (Super-Admin)",  accent: "red"    },
  training:  { order: 10, label: "Schulungs-Modus",           accent: "violet" },
};

const previousMonthLabel = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${MONTH_LABELS_DE[d.getMonth()]} ${d.getFullYear()}`;
};

const downloadCommissionCsv = async () => {
  try {
    const token = localStorage.getItem("novatus_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manager/commissions-csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Download fehlgeschlagen");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    a.href = url;
    a.download = `provisionen_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert(e.message || "Fehler beim Download");
  }
};

export default function Dashboard() {
  const { user } = useAuth();
  const { has: hasCap } = useTenantCapabilities();
  const { hasCap: hasUserCap } = useUserCaps();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [demoResetting, setDemoResetting] = useState(false);
  const [sabineResetting, setSabineResetting] = useState(false);
  const { enabled: trainingEnabled, setEnabled: setTrainingEnabled } = useTrainingMode();
  const [trainingTogglePending, setTrainingTogglePending] = useState(false);
  const [schulungOnboardingOpen, setSchulungOnboardingOpen] = useState(false);
  // Admin und Vertriebsdirektor dürfen den Schulungs-Modus steuern.
  const canManageTraining = isDirectorOrAbove(user);
  const isManagerView = isManagerOrAbove(user);
  // Pure Telesales roles get a stripped-down, telesales-focused dashboard
  // (no projects, no WP-Katalog, no Schulungsquiz). Admins keep the full UI.
  const isTelesalesOnly = user?.role === "agent" || user?.role === "telesales_director";

  // Akademie-Link: nach Logout zurück zur aktuellen Sales-Pro-Instanz
  // (Preview ↔ Production). Die Akademie liest `return_to` aus der URL.
  const academyHref = useMemo(() => {
    const base = process.env.REACT_APP_ACADEMY_URL || "https://novatusakademie.pro";
    const returnTo = typeof window !== "undefined" ? window.location.origin : "";
    return returnTo ? `${base}?return_to=${encodeURIComponent(returnTo)}` : base;
  }, []);

  // Iter88zbu — Dashboard-Reload nach Save irgendwo im System.
  const dashboardReload = useCallback(() => {
    if (!isTelesalesOnly) {
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/projects").then((r) => {
        const list = r.data || [];
        setAllProjects(list);
        setRecent(list.slice(0, 3));
      }).catch(() => {});
    }
    if (isDirectorOrAbove(user)) {
      api.get("/users")
        .then((r) => setPendingUsersCount((r.data || []).filter((u) => u.status === "pending").length))
        .catch(() => setPendingUsersCount(0));
    }
  }, [isTelesalesOnly, user]);
  useReloadBus("*", dashboardReload);

  useEffect(() => {
    // Telesales-only users (agent / telesales_director) do not deal with
    // projects → skip the project-centric endpoints entirely to avoid 403s
    // and unnecessary chatter.
    if (!isTelesalesOnly) {
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/projects").then((r) => {
        const list = r.data || [];
        setAllProjects(list);
        setRecent(list.slice(0, 3));
      }).catch(() => {});
    }
    // Admin: fetch pending user count for the badge
    if (isDirectorOrAbove(user)) {
      api.get("/users")
        .then((r) => setPendingUsersCount((r.data || []).filter((u) => u.status === "pending").length))
        .catch(() => setPendingUsersCount(0));
    }
    // One-time hint for the new external-calendar-sync feature
    if (user?.id && !localStorage.getItem("novatus_ics_hint_seen")) {
      const timer = setTimeout(() => {
        toast.info("Neu: Termine in Outlook · Google · Apple synchronisieren", {
          description: "Dein Novatus-Kalender kann jetzt extern abonniert werden — keine doppelte Pflege mehr.",
          duration: 12000,
          action: {
            label: "Jetzt einrichten",
            onClick: () => navigate("/profile?ics=1"),
          },
        });
        localStorage.setItem("novatus_ics_hint_seen", "1");
      }, 1800);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const resetDemoDataset = async () => {
    if (!window.confirm(
      "Demo-Datensatz Hans Maier zurücksetzen?\n\n" +
      "Tracker → 'Auftrag unterschrieben', Anzahlungsrechnungen und " +
      "Installation-Übergaben werden geleert. Das echte Projekt bleibt " +
      "erhalten, die Schulungs-Story startet neu."
    )) return;
    setDemoResetting(true);
    try {
      const r = await api.post("/admin/demo/reset-hans");
      const d = r.data || {};
      if (d.ok) {
        toast.success(
          `Demo-Datensatz für ${d.customer_name} bereit`,
          { description: "Tracker steht auf 'Auftrag unterschrieben'. Du kannst jetzt den Schulungs-Flow starten.", duration: 6000 },
        );
        if (d.project_id) navigate(`/projects/${d.project_id}`);
      } else {
        toast.error(d.error || "Reset fehlgeschlagen");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fehlgeschlagen");
    } finally {
      setDemoResetting(false);
    }
  };

  const resetSabineLead = async () => {
    if (!window.confirm(
      "Erstkontakt-Lead 'Sabine Schulte' zurücksetzen?\n\n" +
      "Tracker → 'Lead'. Verbrauch, Tech-Check, Fotos, Videos, Skizzen und " +
      "Angebote werden gelöscht. Trainee startet von der Vor-Ort-Aufnahme.\n\n" +
      "Falls noch nicht vorhanden, wird der Lead automatisch angelegt."
    )) return;
    setSabineResetting(true);
    try {
      const r = await api.post("/admin/demo/reset-sabine");
      const d = r.data || {};
      if (d.ok) {
        toast.success(
          `Erstkontakt-Lead '${d.customer_name}' bereit`,
          { description: "Trainee startet bei der Vor-Ort-Aufnahme – Foto, Tech-Check, Heizlast, Angebot.", duration: 6000 },
        );
        if (d.project_id) navigate(`/projects/${d.project_id}`);
      } else {
        toast.error(d.error || "Reset fehlgeschlagen");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fehlgeschlagen");
    } finally {
      setSabineResetting(false);
    }
  };

  const toggleTrainingMode = async () => {
    const turningOn = !trainingEnabled;
    const confirmText = turningOn
      ? "Schulungs-Modus AKTIVIEREN?\n\nWährend der Schulung sehen ALLE Nutzer ausschließlich die beiden Demo-Projekte (Hans Maier + Sabine Schulte). Echte Kundendaten sind für die Dauer der Schulung systemweit ausgeblendet – DSGVO-konform."
      : "Schulungs-Modus DEAKTIVIEREN?\n\nEchte Projekte sind sofort wieder für alle Nutzer sichtbar.";
    if (!window.confirm(confirmText)) return;
    setTrainingTogglePending(true);
    try {
      await setTrainingEnabled(turningOn);
      toast.success(
        turningOn ? "Schulungs-Modus aktiviert" : "Schulungs-Modus deaktiviert",
        { description: turningOn
            ? "Alle Nutzer sehen jetzt ausschließlich die Demo-Projekte."
            : "Echte Vertriebsdaten sind wieder sichtbar.",
          duration: 5000 },
      );
      // Beim ersten Aktivieren: kurzes Onboarding-Modal anzeigen.
      if (turningOn && !hasSeenSchulungOnboarding(user?.id)) {
        setSchulungOnboardingOpen(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wechsel fehlgeschlagen");
    } finally {
      setTrainingTogglePending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Telesales-only roles (agent / telesales_director) get a dedicated,
          stripped-down dashboard — no projects, no WP-Katalog, no Pönalen.
          Admins explicitly skip this branch and see the full UI. */}
      {isTelesalesOnly ? (
        <TelesalesDashboard />
      ) : (
      <>
      <AppHeader />

      <main className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        <PasskeyPromptBanner />

        <div className="mb-8">
          <p className="font-mono text-xs text-[#00E575] tracking-[0.3em] uppercase mb-2">Wärmepumpe</p>
          <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Hallo {user?.first_name}.</h1>
          <p className="text-zinc-400 mt-2">Schnell zum nächsten Projekt – mobil, offlinefähig, direkt vor Ort.</p>
        </div>

        {/* Iter88zbq (2026-02-19) — Einheitliche Projekt-Pipeline mit Suche
            (Cmd+K) und Filtern. Ersetzt NICHT die Kacheln unten, ergänzt sie
            aber als Fast-Access-Zeile. Rep sieht sein Projekt sofort ohne
            Umweg über eine WP-Kachel oder PV-Kachel. */}
        {!isTelesalesOnly && allProjects.length > 0 && (
          <ProjectPipeline projects={allProjects} showRanking={isDirectorOrAbove(user)} />
        )}

        {/* ===== MANAGER / DIRECTOR / ADMIN: GRID LAYOUT ===== */}
        {isManagerView && (
          <>
            {/* Primary CTA — full-width emphasized */}
            <Link
              to="/projects/new"
              data-testid="dashboard-new-project-btn"
              className="flex items-center justify-center gap-2 mb-3 bg-gradient-to-r from-[#00E575] to-[#00C964] text-[#09090B] rounded-xl px-4 h-14 active:scale-[0.99] transition glow-green hover:shadow-[0_0_20px_rgba(0,229,117,0.35)] font-bold text-[15px]"
            >
              <Plus size={20} /> Neues Projekt anlegen
            </Link>

            {/* Grouped management grid — thematic blocks, color-coded */}
            <DashboardManagerNav
              groups={DASHBOARD_GROUPS}
              items={(() => {
                const tiles = [
                  // ── Mein Tag ──────────────────────────────────────────
                  { group: "daily", testid: "dashboard-tasks-link", to: "/tasks", icon: ListTodo, label: "Aufgaben" },
                  { group: "daily", testid: "dashboard-calendar-link", to: "/calendar", icon: CalendarIcon, label: "Kalender" },
                  { group: "daily", testid: "dashboard-billing-link", to: "/abo", icon: Receipt, label: "Mein Abo" },
                  { group: "daily", testid: "admin-link-allprojects", to: "/projects", icon: TableIcon, label: "Alle Projekte" },
                  { group: "daily", testid: "admin-link-kanban", to: "/manager/kanban", icon: Settings, label: "Kanban" },
                  { group: "daily", testid: "admin-link-bza-kanban", to: "/kanban-bza", icon: AlertTriangle, label: "BzA-Prozess" },
                  ...(isAdmin(user) || isDirectorOrAbove(user) ? [
                    { group: "daily", testid: "admin-link-project-stages", to: "/admin/project-stages", icon: Settings, label: "Prozess-Konfig" },
                    { group: "admin", testid: "admin-link-manual-signatures", to: "/admin/manual-signatures", icon: FileText, label: "Signaturen (DSGVO)" },
                    { group: "admin", testid: "admin-link-signing-options", to: "/admin/signing-options", icon: FileText, label: "Signatur-Optionen" },
                  ] : []),
                  ...(user?.role === "super_admin" || user?.is_super_admin ? [
                    { group: "admin", testid: "admin-link-membership-margins", to: "/super-admin/membership-margins", icon: AlertTriangle, label: "Membership-Margen", accent: "amber" },
                  ] : []),

                  // ── Operations & Pipeline ────────────────────────────
                  ...(hasUserCap("see_intake_pipeline") ? [
                    { group: "ops", testid: "admin-link-intake-pipeline", to: "/pipeline/intake", icon: Inbox, label: "Intake-Pipeline", accent: "green" },
                  ] : []),
                  ...(isDirectorOrAbove(user) ? [
                    { group: "ops", testid: "admin-link-health", to: "/admin/health", icon: AlertTriangle, label: "Pipeline-Health", accent: "green" },
                    { group: "ops", testid: "admin-link-leads", to: "/admin/orientierungsangebote", icon: Inbox, label: "Orient.-Angebote", accent: "amber" },
                    { group: "ops", testid: "admin-link-auction-stats", to: "/admin/auction-stats", icon: Gavel, label: "Auktions-Stats", accent: "green" },
                    { group: "ops", testid: "admin-link-penalties", to: "/admin/penalties", icon: AlertOctagon, label: "Pönalen", accent: "amber" },
                  ] : []),
                  ...(isDirectorOrAbove(user) ? [
                    { group: "ops", testid: "admin-link-back-office", to: "/admin/back-office", icon: ClipboardList, label: "Back-Office", accent: "green" },
                    { group: "ops", testid: "admin-link-offers", to: "/admin/offers", icon: FileText, label: "Angebote", accent: "green" },
                    { group: "ops", testid: "admin-link-signature-audit", to: "/admin/signature-audit", icon: ShieldCheck, label: "Signatur-Audit", accent: "green" },
                  ] : []),

                  // ── Personen & Teams ─────────────────────────────────
                  ...(hasUserCap("manage_users") ? [
                    { group: "people", testid: "admin-link-users", to: "/admin/users", icon: UsersIcon, label: "Benutzer", badge: pendingUsersCount > 0 ? pendingUsersCount : null, accent: pendingUsersCount > 0 ? "amber" : "sky" },
                    { group: "people", testid: "admin-link-users-table", to: "/admin/users-table", icon: TableIcon, label: "Benutzer-Tab.", accent: "sky" },
                    { group: "people", testid: "admin-link-hierarchy", to: "/admin/hierarchy", icon: Network, label: "Hierarchie", accent: "sky" },
                    { group: "people", testid: "admin-link-branches", to: "/admin/branches", icon: Building2, label: "Niederlassungen", accent: "sky" },
                  ] : []),
                  ...(hasUserCap("see_team_analytics") ? [
                    { group: "people", testid: "admin-link-team", to: "/manager/team", icon: UsersIcon, label: "Team-Auswertung" },
                    { group: "people", testid: "admin-link-sachb-stats", to: "/admin/sachbearbeiter-stats", icon: BarChart3, label: "Sachbearbeiter", accent: "amber" },
                  ] : []),
                  ...(hasUserCap("manage_team") ? [
                    { group: "people", testid: "admin-link-sales-teams", to: "/admin/sales-teams", icon: UsersIcon, label: "Sales-Teams", accent: "sky" },
                    { group: "people", testid: "admin-link-installers", to: "/admin/installers", icon: Wrench, label: "Installation", accent: "sky" },
                    { group: "people", testid: "admin-link-installer-apps", to: "/admin/installer-applications", icon: Wrench, label: "Inst.-Bewerb.", accent: "amber" },
                  ] : []),

                  // ── Kataloge & Produkte ──────────────────────────────
                  // Iter88zbs (2026-02-20) — Konsolidiert: EINE Kachel „Produkte"
                  // führt auf /admin/produkte mit Tabs für PV-Komponenten,
                  // PV-Pakete, WP-Hersteller, WP-Komponenten, WP-Pakete.
                  // Vorher: 4 einzelne Kacheln, jede mit eigener Seite.
                  ...((hasUserCap("manage_wp_catalog") || hasUserCap("manage_pv_catalog")) ? [
                    { group: "catalog", testid: "admin-link-produkte", to: "/admin/produkte", icon: Package, label: "Produkte (PV & WP)", accent: "amber" },
                  ] : []),

                  // ── System-Konfiguration ─────────────────────────────
                  ...(isDirectorOrAbove(user) ? [
                    { group: "config", testid: "admin-link-system-health", to: "/admin/system-health", icon: ShieldCheck, label: "System-Health", accent: "violet" },
                    { group: "config", testid: "admin-link-test-data", to: "/admin/test-data", icon: Eraser, label: "Test-Daten-Manager", accent: "violet" },
                    { group: "config", testid: "admin-link-invoice-tools", to: "/admin/invoice-tools", icon: RefreshCw, label: "Rechnungs-PDF-Werkzeuge", accent: "violet" },
                    { group: "config", testid: "admin-link-email-logs", to: "/admin/email-logs", icon: Mail, label: "E-Mail-Sende-Übersicht", accent: "violet" },
                    { group: "config", testid: "admin-link-document-templates", to: "/admin/document-templates", icon: FileText, label: "Dokumenten-Verwaltung", accent: "emerald" },
                    { group: "config", testid: "admin-link-text-blocks", to: "/admin/text-blocks", icon: FileText, label: "Textbausteine", accent: "emerald" },
                    { group: "config", testid: "admin-link-telesales-settings", to: "/admin/telesales-settings", icon: Phone, label: "Telesales-Setup", accent: "violet" },
                  ] : []),
                  ...(["admin", "sales_director", "sales_manager"].includes(user?.role) ? [
                    { group: "config", testid: "admin-link-vcheck-rules", to: "/admin/vcheck-pflichtfelder", icon: ListChecks, label: "V-Check Pflichtfelder", accent: "emerald" },
                  ] : []),
                  ...(hasUserCap("manage_routing_rules") ? [
                    { group: "config", testid: "admin-link-routing-rules", to: "/admin/routing-rules", icon: GitBranch, label: "Routing-Regeln", accent: "violet" },
                  ] : []),
                  ...(hasUserCap("manage_api_keys") ? [
                    { group: "config", testid: "admin-link-apikeys", to: "/admin/api-keys", icon: Key, label: "API-Keys", accent: "violet" },
                  ] : []),
                  ...(hasUserCap("manage_provision_and_discount") && isDirectorOrAbove(user) ? [
                    { group: "config", testid: "admin-link-sales-settings", to: "/manager/sales-settings", icon: Percent, label: "Provision", accent: "violet" },
                  ] : []),

                  // ── Marketing & Statistik ────────────────────────────
                  ...(isDirectorOrAbove(user) ? [
                    { group: "marketing", testid: "admin-link-sms-stats", to: "/admin/sms-stats", icon: MessageSquare, label: "SMS-Statistik", accent: "amber" },
                    { group: "marketing", testid: "admin-link-briefing-ab", to: "/admin/briefing-ab-stats", icon: FlaskConical, label: "Briefing A/B", accent: "amber" },
                    { group: "marketing", testid: "admin-link-empfehlungsbonus-analytics", to: "/admin/empfehlungsbonus-analytics", icon: Gift, label: "Empfehlungsboni", accent: "amber" },
                  ] : []),
                  ...(hasUserCap("manage_promo_codes") ? [
                    { group: "marketing", testid: "admin-link-promo-codes", to: "/admin/promo-codes", icon: Percent, label: "Promo-Codes", accent: "amber" },
                  ] : []),
                  { group: "marketing", testid: "admin-link-feedback", to: "/admin/feedback", icon: MessageSquare, label: "Feedback" },
                  ...(isDirectorOrAbove(user) ? [
                    { group: "marketing", testid: "dashboard-commission-csv-btn", onClick: downloadCommissionCsv, icon: Download, label: "Provis-CSV" },
                  ] : []),

                  // ── Lernen ───────────────────────────────────────────
                  { group: "learning", testid: "dashboard-academy-link", href: academyHref, external: true, icon: GraduationCap, label: "Akademie", accent: "green" },
                  { group: "learning", testid: "dashboard-quiz-link", to: "/quiz", icon: GraduationCap, label: "Schulungsquiz" },
                  { group: "learning", testid: "dashboard-novatus-care-link", href: process.env.REACT_APP_NOVATUS_CARE_URL || "https://novatus-care.de", external: true, icon: Heart, label: "Novatus Care", accent: "amber" },
                  ...(isDirectorOrAbove(user) ? [
                    { group: "learning", testid: "admin-link-handbook", to: "/admin/handbook", icon: BookOpen, label: "Handbuch" },
                    { group: "learning", testid: "admin-link-quiz-results", to: "/admin/quiz-results", icon: GraduationCap, label: "Quiz-Auswert." },
                  ] : []),

                  // ── Partner & Mandanten ──────────────────────────────
                  ...(isDirectorOrAbove(user) ? [
                    { group: "partners", testid: "admin-link-tenants", to: "/admin/tenants", icon: Building2, label: "Mandanten", accent: "violet" },
                    { group: "partners", testid: "admin-link-partner-profiles", to: "/admin/partner-profiles", icon: Building2, label: "Partner-Profile", accent: "violet" },
                  ] : []),

                  // ── Sicherheit (Super-Admin only) ────────────────────
                  ...(user?.is_super_admin ? [
                    { group: "security", testid: "admin-link-security-audit", to: "/admin/security-audit", icon: Shield, label: "Security-Audit", accent: "red" },
                    { group: "security", testid: "admin-link-audit-scores", to: "/admin/audit-scores", icon: BarChart3, label: "DSGVO-Scores", accent: "red" },
                  ] : []),
                  // ── Compliance (Tenant-Admin + Super-Admin) ──────────
                  ...(isDirectorOrAbove(user) ? [
                    { group: "security", testid: "admin-link-compliance", to: "/admin/compliance", icon: ShieldCheck, label: "AVV & Verträge", accent: "red" },
                  ] : []),

                  // ── Schulungs-Modus ──────────────────────────────────
                  ...(canManageTraining ? [
                    { group: "training", testid: "dashboard-training-toggle", onClick: toggleTrainingMode, icon: trainingEnabled ? Lock : Unlock, label: `Schulung ${trainingEnabled ? "EIN" : "AUS"}`, accent: trainingEnabled ? "violet" : null },
                  ] : []),
                  ...(canManageTraining && trainingEnabled ? [
                    { group: "training", testid: "dashboard-demo-reset-btn", onClick: resetDemoDataset, icon: GraduationCap, label: "Demo · Hans", accent: "violet" },
                    { group: "training", testid: "dashboard-demo-sabine-btn", onClick: resetSabineLead, icon: GraduationCap, label: "Demo · Sabine", accent: "violet" },
                  ] : []),
                ];

                // Innerhalb jeder Gruppe alphabetisch sortieren – die Gruppen-
                // Reihenfolge wird im DashboardManagerNav anhand DASHBOARD_GROUPS bestimmt.
                return tiles.sort((a, b) => {
                  if (a.group !== b.group) return 0;
                  return a.label.localeCompare(b.label, "de", { sensitivity: "base" });
                });
              })()}
            />

            {/* Slim pending-installer banner (director+admin) */}
            {isDirectorOrAbove(user) && <PendingInstallerBanner />}

            {(isAdmin(user) || isDirectorOrAbove(user)) && <SmsHealthWarning />}
            {isAdmin(user) && <UserActivityTile />}

            {/* Compact stats — half-height */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-6">
              <StatCard testid="stat-total" icon={FolderOpen} label="Projekte gesamt" value={stats?.total_projects ?? 0} accent="text-zinc-300" compact />
              <StatCard testid="stat-in-progress" icon={TrendingUp} label="In Bearbeitung" value={stats?.in_progress ?? 0} accent="text-blue-400" compact />
              <StatCard testid="stat-offers" icon={FileText} label="Angebote" value={stats?.offers_made ?? 0} accent="text-amber-400" compact />
              <StatCard testid="stat-won" icon={Trophy} label="Gewonnen" value={stats?.won ?? 0} accent="text-[#00E575]" compact />
            </div>

            {/* Project table with sort, filter, swipe-actions */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-semibold">Alle Projekte</h2>
              <Link to="/projects" data-testid="dashboard-all-projects-link-mgr" className="text-xs text-[#00E575] hover:underline inline-flex items-center gap-1">
                Detail-Ansicht <ArrowRight size={12} />
              </Link>
            </div>
            <ManagerProjectTable projects={allProjects} />
          </>
        )}

        {/* ===== SALES REP / INSTALLER: ORIGINAL PILL-STRIP LAYOUT ===== */}
        {!isManagerView && (
        <>
        <div className="-mx-4 px-4 mb-6">
          <div
            data-testid="dashboard-nav-strip"
            className="flex items-stretch gap-2.5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* Primary CTA — Neues Projekt */}
            {user?.role !== "installer" && (
              <Link
                to="/projects/new"
                data-testid="dashboard-new-project-btn"
                className="snap-start shrink-0 inline-flex items-center gap-2 bg-gradient-to-r from-[#00E575] to-[#00C964] text-[#09090B] rounded-xl px-4 h-12 active:scale-[0.97] transition glow-green hover:shadow-[0_0_20px_rgba(0,229,117,0.35)] font-bold text-[14px]"
              >
                <Plus size={16} className="text-[#09090B]" />
                Neues Projekt
              </Link>
            )}

            {/* Installer primary CTA — Meine Aufträge */}
            {user?.role === "installer" && (
              <Link
                to="/installer/jobs"
                data-testid="dashboard-installer-jobs-btn"
                className="snap-start shrink-0 inline-flex items-center gap-2 bg-gradient-to-r from-[#00E575] to-[#00C964] text-[#09090B] rounded-xl px-4 h-12 active:scale-[0.97] transition glow-green hover:shadow-[0_0_20px_rgba(0,229,117,0.35)] font-bold text-[14px]"
              >
                <Wrench size={16} className="text-[#09090B]" />
                Meine Aufträge
              </Link>
            )}
            {user?.role === "installer" && (
              <NavPill testid="dashboard-installer-auctions" to="/installer/auctions" icon={Gavel} label="Auktionen" accent="green" />
            )}

            {(user?.role === "admin" || user?.is_super_admin) && hasCap("telesales_capture") && (
              <NavPill testid="dashboard-telesales-link" to="/telesales" icon={Phone} label="Telesales-Capture" accent="green" />
            )}
            {(user?.role === "telesales_director" || user?.role === "admin" || user?.is_super_admin) && (hasCap("telesales_director_review") || user?.role === "admin" || user?.is_super_admin) && (
              <NavPill testid="dashboard-telesales-director-link" to="/telesales/director" icon={Phone} label="Telesales-Direktor" accent="amber" />
            )}
            {(user?.role === "telesales_director" || user?.role === "admin" || user?.is_super_admin) && (hasCap("telesales_lists") || user?.role === "admin" || user?.is_super_admin) && (
              <NavPill testid="dashboard-call-lists-link" to="/admin/call-lists" icon={Phone} label="Call-Listen" />
            )}

            {(user?.role === "sales_rep" || user?.role === "sales_manager" || user?.role === "sales_director" || user?.role === "admin") && hasCap("sales_pipeline") && (
              <NavPill testid="dashboard-lead-auctions-link" to="/lead-auctions" icon={Gavel} label="Lead-Auktionen" />
            )}

            {/* Iter88w (2026-02-19): Für Rollen `agent` (Telesales-Innendienst)
                und `installer` werden die generischen Kacheln (Aufgaben, Kalender,
                Quiz, Akademie, Care, Abo) ausgeblendet — sie sollen sich
                ausschließlich auf ihre Kern-Workflows konzentrieren. Sales-Rollen
                und Admin sehen sie weiterhin. */}
            {!isTelesalesOnly && user?.role !== "installer" && (
              <>
                <NavPill testid="dashboard-tasks-link" to="/tasks" icon={ListTodo} label="Aufgaben" />
                <NavPill testid="dashboard-calendar-link" to="/calendar" icon={CalendarIcon} label="Kalender" />
                <NavPill testid="dashboard-quiz-link" to="/quiz" icon={GraduationCap} label="Schulungsquiz" />
                <NavPill testid="dashboard-academy-link" href={academyHref} icon={GraduationCap} label="Akademie" external accent="green" />
                <NavPill testid="dashboard-novatus-care-link" href={process.env.REACT_APP_NOVATUS_CARE_URL || "https://novatus-care.de"} icon={Heart} label="Novatus Care" external accent="amber" />
                <NavPill testid="dashboard-billing-link" to="/abo" icon={Receipt} label="Mein Abo" />
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <StatCard testid="stat-total" icon={FolderOpen} label="Projekte gesamt" value={stats?.total_projects ?? 0} accent="text-zinc-300" />
          <StatCard testid="stat-in-progress" icon={TrendingUp} label="In Bearbeitung" value={stats?.in_progress ?? 0} accent="text-blue-400" />
          <StatCard testid="stat-offers" icon={FileText} label="Angebote" value={stats?.offers_made ?? 0} accent="text-amber-400" />
          <StatCard testid="stat-won" icon={Trophy} label="Gewonnen" value={stats?.won ?? 0} accent="text-[#00E575]" />
        </div>

        {/* Recent projects */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold">Aktuelle Projekte</h2>
          <Link to="/projects" data-testid="dashboard-all-projects-link" className="text-sm text-[#00E575] hover:underline inline-flex items-center gap-1 py-2 min-h-[40px]">
            Alle ansehen <ArrowRight size={14} />
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400">
            Noch keine Projekte. Legen Sie das erste Projekt oben an.
          </div>
        ) : (
          <div className="space-y-2">
            {recent.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                data-testid={`dashboard-project-${p.id}`}
                className="block bg-[#18181B] border border-zinc-800 rounded-2xl p-4 hover:border-[#00E575]/50 transition active:scale-[0.99]"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.customer_name || "—"}</div>
                    <div className="text-xs text-zinc-400 font-mono">{p.project_number} · {p.customer_zip} {p.customer_city}</div>
                    <OfferLineChips lines={p.offer_lines} projectId={p.id} />
                  </div>
                  <span className={`px-2.5 py-1 rounded-md text-[12px] font-medium uppercase tracking-wider whitespace-nowrap ${STATUS_COLORS[p.status] || "bg-zinc-700"}`}>{p.status}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
        </>
        )}

        {/* Admin / Manager quick links — moved into the top horizontal scroll-strip.
            (Bottom Verwaltung grid removed in favour of single-row nav above.) */}
      </main>
      </>
      )}
      <SchulungOnboardingModal
        open={schulungOnboardingOpen}
        onClose={() => setSchulungOnboardingOpen(false)}
        userId={user?.id}
      />
    </div>
  );
}

// WP / PV / beides — aus den aktiven Angeboten des Projekts
function OfferLineChips({ lines = [], projectId }) {
  if (!lines.length) {
    return <div className="mt-1.5 text-[11px] text-zinc-500" data-testid={`dashboard-project-lines-${projectId}`}>Noch kein Angebot</div>;
  }
  return (
    <div className="mt-1.5 flex gap-1.5" data-testid={`dashboard-project-lines-${projectId}`}>
      {lines.includes("wp") && (
        <span data-testid={`dashboard-project-wp-${projectId}`}
              className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-bold bg-orange-500/15 text-orange-300 border border-orange-500/40">
          <Flame size={11} /> WP
        </span>
      )}
      {lines.includes("pv") && (
        <span data-testid={`dashboard-project-pv-${projectId}`}
              className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-bold bg-amber-400/15 text-amber-200 border border-amber-400/40">
          <Sun size={11} /> PV
        </span>
      )}
    </div>
  );
}
